import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true, // Send httpOnly cookies with every request
  timeout: 30000, // 30 second timeout
});

// CSRF token management
let csrfToken = null;
let csrfFetchPromise = null;

async function fetchCsrfToken() {
  const res = await axios.get((process.env.REACT_APP_API_URL || '/api') + '/csrf-token', { withCredentials: true });
  csrfToken = res.data.csrfToken;
  return csrfToken;
}

async function ensureCsrfToken() {
  if (csrfToken) return csrfToken;

  // Prevent concurrent fetches (multiple POSTs racing)
  if (csrfFetchPromise) return csrfFetchPromise;

  csrfFetchPromise = fetchCsrfToken().finally(() => {
    csrfFetchPromise = null;
  });

  return csrfFetchPromise;
}

// Clear cached CSRF token (called on 403 so next request re-fetches)
function clearCsrfToken() {
  csrfToken = null;
}

// Attach CSRF token to state-changing requests
api.interceptors.request.use(async (config) => {
  if (['post', 'put', 'patch', 'delete'].includes(config.method)) {
    const token = await ensureCsrfToken();
    config.headers['X-CSRF-Token'] = token;
  }
  return config;
});

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // If 2FA is required, redirect to security page immediately
    if (error.response?.status === 403 && error.response?.data?.error === '2FA_REQUIRED') {
      window.location.href = '/security';
      return Promise.reject(error);
    }

    // On CSRF failure, clear the cached token and retry once
    if (error.response?.status === 403 && !originalRequest._csrfRetry) {
      const errorMsg = error.response?.data?.error || '';
      if (errorMsg.includes('CSRF')) {
        originalRequest._csrfRetry = true;
        clearCsrfToken();
        const token = await ensureCsrfToken();
        originalRequest.headers['X-CSRF-Token'] = token;
        return api(originalRequest);
      }
    }

    // If 401 and not already retrying, attempt token refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Don't retry auth endpoints that are expected to return 401
      if (
        originalRequest.url?.includes('/auth/login') ||
        originalRequest.url?.includes('/auth/register') ||
        originalRequest.url?.includes('/auth/refresh') ||
        originalRequest.url?.includes('/auth/logout') ||
        originalRequest.url?.includes('/auth/me') ||
        originalRequest.url?.includes('/auth/accept-invite') ||
        originalRequest.url?.includes('/auth/reset-password')
      ) {
        // Only hard-redirect for refresh failures (not initial session checks)
        if (originalRequest.url?.includes('/auth/refresh')) {
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => api(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await api.post('/auth/refresh');
        processQueue(null);
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
